import { BookingType } from "../../../../../features/Booking-Form/typings/booking-inputs";
import {
  SelectedZoneOrWorkplace,
  SelectedZoneOrWorkplaceType
} from "../../../../../features/Reports/slices/report.slice";
import { FloorPlanMemo } from "../../../../../features/Reports/typings/reports.types";
import { typeIdPerBookingMode } from "../../../../BookingForm/functions/form.functions";
import { BookingProperty } from "../../../../FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";
import { EquipmentCategory } from "../../../../FacilityManager/Domain/Types/FloorPlan/EquipmentCategory.type";
import { IFloorPayload } from "../../../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { IPlaceCategory } from "../../../../FacilityManager/Domain/Types/FloorPlan/PlaceCategory.type";
import { IPlaceSchedule } from "../../../../FacilityManager/Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneCategory } from "../../../../FacilityManager/Domain/Types/FloorPlan/ZoneCategory.type";
import { IZoneSchedule } from "../../../../FacilityManager/Domain/Types/FloorPlan/ZoneSchedule";
import { FilterTypState } from "../facility-report-filter-and-selector.component";
import * as _ from "lodash";

type Props = {
  floorData: FloorPlanMemo;
  filterType: FilterTypState[];
  currentFloors: { id: number; open: boolean }[];
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace;
  selectedBookingType: BookingType | null;
};
/** function to find attributes from the floor plan based on the booking type selection */
export function findAttributesBySelection({
  floorData,
  currentFloors,
  selectedBookingType,
  selectedZoneOrWorkplace
}: Props) {
  const [type, typeId] = typeIdPerBookingMode(selectedBookingType);

  let entries: (IPlaceSchedule | IZoneSchedule)[] = [];

  let floorPlanData = [floorData[currentFloors[0].id].floorData];
  if (!!currentFloors[1] && currentFloors[1].open) {
    floorPlanData = [...floorPlanData, floorData[currentFloors[1].id].floorData];
  }

  // search entries by selected type or selected zone or workplace
  entries = returnEntriesBySelected(floorPlanData, type, typeId, selectedZoneOrWorkplace);

  const bookingProperties = filterUndefinedOrNull(
    _.uniqBy(
      entries.flatMap(ent => ent.bookingProperties),
      item => item?.id
    )
  ) as BookingProperty[];

  const categoryType = type;
  const categories = filterUndefinedOrNull(
    _.uniqBy(
      entries.flatMap(ent => ent.category),
      item => item?.id
    )
  ) as (IPlaceCategory | IZoneCategory)[];

  const equipmentCategories = filterUndefinedOrNull(
    _.uniqBy(
      entries.flatMap(ent => ent.equipmentCategories),
      item => item?.id
    )
  ) as (EquipmentCategory & { quantity: number })[];

  const equipment = _.uniqBy(
    entries.flatMap(ent => ent.equipments ?? []),
    item => item.id
  ).map(({ id, name }) => ({ id, name }));

  return { bookingProperties, categories, categoryType, equipmentCategories, equipment };
}

export const getEntriesByType = (
  items: any[],
  typeId: number,
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace,
  matchIdFn: (item: any) => number | undefined,
  matchTypeFn: (item: any) => number | undefined,
  assignedPlaces?: any[]
) => {
  const baseFiltered = items.filter(item => {
    if (selectedZoneOrWorkplace?.open) {
      const matchedId = matchIdFn(item);
      return matchedId !== undefined && selectedZoneOrWorkplace.id.includes(matchedId);
    }
    return matchTypeFn(item) === typeId;
  });

  // removed the ai-supported condition '&& items === assignedPlaces'
  if (assignedPlaces && assignedPlaces.length > 0) {
    // prevent duplicates if assignedPlaces are already in baseFiltered
    const existingIds = new Set(baseFiltered.map(item => item.id));
    const uniqueAssigned = assignedPlaces.filter(p => !existingIds.has(p.id));
    return [...baseFiltered, ...uniqueAssigned];
  }

  return baseFiltered;
};

export const returnEntriesBySelected = (
  floorPlanData: IFloorPayload[],
  type: "place" | "zone",
  typeId: number,
  selectedZoneOrWorkplace: SelectedZoneOrWorkplace
): (IPlaceSchedule | IZoneSchedule)[] => {
  let assignedPlaces: IPlaceSchedule[] = [];

  if (
    selectedZoneOrWorkplace &&
    selectedZoneOrWorkplace.open &&
    selectedZoneOrWorkplace.type === SelectedZoneOrWorkplaceType.ZONE
  ) {
    const zoneScheduleIds = new Set(
      floorPlanData
        .flatMap(data =>
          data.zones.filter(zone =>
            selectedZoneOrWorkplace.id.includes(zone.inventory?.id || zone.inventoryId)
          )
        )
        .map(zone => zone.id)
    );
    assignedPlaces = floorPlanData.flatMap(data =>
      data.places.filter(place => zoneScheduleIds.has(place.zoneScheduleId))
    );
  }

  return floorPlanData.flatMap(data => {
    if (type === "place") {
      return getEntriesByType(
        data.places,
        typeId,
        selectedZoneOrWorkplace,
        /* istanbul ignore next */
        place => place.inventory?.id || place.inventoryId, // matchIdFn
        place => place.inventory?.placeTypeId, // matchTypeFn
        assignedPlaces
      );
    } else {
      // type === "zone"
      return getEntriesByType(
        data.zones,
        typeId,
        selectedZoneOrWorkplace,
        /* istanbul ignore next */
        zone => zone.inventory?.id || zone.inventoryId, // matchIdFn
        zone => zone.inventory?.zoneTypeId // matchTypeFn
      );
    }
  });
};

export const filterUndefinedOrNull = <T>(entries: (T | null | undefined)[]) =>
  entries.filter((item): item is T => item !== null && item !== undefined);
